import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { PluginComponent } from 'plugins';
import Icon from 'components/Icon';
import { openPaymentModal, openModalPage } from 'actions/modalPage';
import { addWarning, dismissType } from 'actions/Error';
import { cancelOrderAction } from 'actions/sales';
import { printReceipt } from 'actions/integrations/printer';
import { modalPages } from 'constants/modalPage';
import { useAsyncFunctionNoParallelExecution } from 'utils/hooks/useSingleUseFunction';
import { createConfirmation } from 'actions/Confirmation';
import { updateOrderAmount } from 'actions/ShoppingCart';
import { setPaymentButtonClicked } from 'actions/Payments';
import { getHasUndismissedErrorsOfType } from 'reducers/Error';
import { getCurrentSalesDocument, getLastSalesDocument } from 'reducers/sales';
import { getIsDefaultCustomer } from 'reducers/customerSearch';
import {
  getSetting,
  getShouldChooseEmployeeManually,
} from 'reducers/configs/settings';
import { useAppDispatch } from 'reducers';
import {
  getHasProductsInShoppingCart,
  getProductsInShoppingCart,
} from 'reducers/ShoppingCart';
import { getIsModalOpened } from 'reducers/modalPage';
import { getHasActiveConfirmation } from 'reducers/Confirmation';
import { isEmpty } from 'utils';
import { useShortcut } from 'utils/hooks/keyboard/useShortcut';
import { KeyboardShortcut } from 'components/KeyboardShortcut';
import { printingStatistics } from 'utils/statistics/PrintingStatistics';
import { STATISTIC_ENTRY_TYPES } from 'utils/statistics/Statistics';

import StyledButton from './StyledButton';

const GreenButton: React.FC<{
  width: number | string;
  height?: number;
  removeMargin?: boolean;
}> = ({ width, height, removeMargin }) => {
  const { t } = useTranslation(['alerts', 'return', 'common', 'gridButtons']);

  const dispatch = useAppDispatch();
  const hasItemsInCart = useSelector(getHasProductsInShoppingCart);
  const items = useSelector(getProductsInShoppingCart);

  const hasSavingDocumentError = useSelector(
    getHasUndismissedErrorsOfType('savingDocument'),
  );

  const { salesDoc: lastInvoice, payments: receiptPayments } = useSelector(
    getLastSalesDocument,
  );

  const isModalOpened = useSelector(getIsModalOpened);
  const activeConfirmation = useSelector(getHasActiveConfirmation);
  const canPrintLastReceipt =
    (lastInvoice.receiptLink || !isEmpty(receiptPayments)) && !hasItemsInCart;
  const isDefaultCustomer = useSelector(getIsDefaultCustomer);
  const disallowDefaultCustomer = useSelector(
    getSetting('touchpos_disable_selling_to_default_customer'),
  );

  const shouldChooseEmployeeManually = useSelector(
    getShouldChooseEmployeeManually,
  );
  const currentSaleDoc = useSelector(getCurrentSalesDocument);

  const [processPayment] = useAsyncFunctionNoParallelExecution(async () => {
    if (hasItemsInCart) {
      if (
        currentSaleDoc.type === 'ORDER' &&
        items.some(item => item.amount < 0)
      ) {
        try {
          await new Promise((resolve, reject) => {
            dispatch(
              createConfirmation(resolve, reject, {
                title: t('alerts.orderNotSupported.title', { ns: 'return' }),
                body: t('alerts.orderNotSupported.body', { ns: 'return' }),
                confirmText: t('yes', { ns: 'common' }),
                cancelText: t('close', { ns: 'common' }),
              }),
            );
          });
          return dispatch(cancelOrderAction(currentSaleDoc));
        } catch (error) {
          await Promise.all(
            items
              .filter(item => item.amount < 0)
              .map(item => {
                const originalRow = currentSaleDoc.rows.find(
                  row => row.stableRowID === item.stableRowID,
                );
                if (!originalRow) {
                  return null;
                }
                return dispatch(
                  updateOrderAmount(originalRow.amount, item.orderIndex),
                );
              })
              .filter(Boolean),
          );
          return dispatch(
            addWarning(t('cartWillBeReset'), { selfDismiss: false }),
          );
        }
      }

      if (isDefaultCustomer && disallowDefaultCustomer) {
        return dispatch(
          addWarning(t('noCustomerSelected'), {
            selfDismiss: true,
            errorType: 'defaultCustomer',
          }),
        );
      }
      dispatch(dismissType('defaultCustomer'));

      if (shouldChooseEmployeeManually) {
        await dispatch(
          openModalPage({
            component: modalPages.employeeSelect,
            isPopup: true,
          }),
        );
      }

      if (!isModalOpened && !activeConfirmation && !hasSavingDocumentError) {
        dispatch(setPaymentButtonClicked(true));
        try {
          return dispatch(
            openPaymentModal({ props: { payButtonClicked: true } }),
          );
        } catch (e) {
          dispatch(setPaymentButtonClicked(false));
        }
      }
    } else if (canPrintLastReceipt) {
      printingStatistics.init({
        description: 'Total print time (from main view green button)',
        itemType: STATISTIC_ENTRY_TYPES.TypeNameTotalTime,
      });
      try {
        return dispatch(
          // @ts-ignore
          printReceipt({ payments: receiptPayments, ...lastInvoice }),
        );
      } finally {
        dispatch(
          printingStatistics.finishLogAndSendToMetrics(
            'Total print time (from main view green button)',
          ),
        );
      }
    } else {
      return dispatch(
        addWarning(t('shoppingCartEmpty'), { selfDismiss: true }),
      );
    }
    return undefined;
  });

  useShortcut('openPayment', canPrintLastReceipt ? null : processPayment, 20);

  return (
    <PluginComponent hookname="UICustomPayButton">
      <StyledButton
        width={width}
        height={height}
        variant="contained"
        onClick={processPayment}
        data-testid="payment-button"
        buttonColor="grid_green"
        removeMargin={removeMargin}
      >
        <span
          style={{
            fontSize: '20px',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          {canPrintLastReceipt ? (
            <Trans i18nKey="actions.printLast" ns="gridButtons">
              <Icon name="icon_printer" />
            </Trans>
          ) : (
            t('actions.pay', { ns: 'gridButtons' })
          )}
        </span>
        {canPrintLastReceipt ? null : (
          <KeyboardShortcut
            className="func-key"
            shortcutAction="openPayment"
            extraStyles={{ fontSize: '13px', fontWeight: 700 }}
          />
        )}
        {/* Temporarily disabled as per https://erply.atlassian.net/browse/PBIB-1750?focusedCommentId=187006 */}
        {/* <TipAfterPayment /> */}
      </StyledButton>
    </PluginComponent>
  );
};

export default GreenButton;
